import React from "react";
import clsx from "clsx";
import { MdChevronRight, MdExpandMore } from "react-icons/md";
import { MenuItem } from "../redux/slices/menuSlice";

interface CategoryFilterProps {
  menuItems: readonly MenuItem[];
  selectedCategory: string | null;
  onCategoryChange: (category: string | null) => void;
}

interface Subcategory {
  name: string;
  count: number;
}

interface CategoryHierarchy {
  name: string;
  count: number;
  subcategories: Subcategory[];
}

// ✅ 1. Extracted AllItemsButton Component
interface AllItemsButtonProps {
  isActive: boolean;
  itemCount: number;
  onClick: () => void;
}

const AllItemsButton = React.memo<AllItemsButtonProps>(({ isActive, itemCount, onClick }) => (
  <button
    onClick={onClick}
    aria-pressed={isActive}
    className={clsx(
      "px-4 py-2 rounded-full flex items-center gap-2 text-sm font-medium whitespace-nowrap transition-colors",
      isActive ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
    )}
  >
    <span>All Items</span>
    <span
      className={clsx(
        "text-xs px-2 py-0.5 rounded-full",
        isActive ? "bg-orange-600 text-white" : "bg-gray-200 text-gray-600"
      )}
    >
      {itemCount}
    </span>
  </button>
));

AllItemsButton.displayName = 'AllItemsButton';

// ✅ 1. Extracted MainCategoryButton Component
interface MainCategoryButtonProps {
  category: CategoryHierarchy;
  isActive: boolean;
  isExpanded: boolean;
  onClick: () => void;
}

const MainCategoryButton = React.memo<MainCategoryButtonProps>(({ 
  category, 
  isActive, 
  isExpanded, 
  onClick 
}) => {
  const hasSubcategories = category.subcategories.length > 0;

  return (
    <button
      onClick={onClick}
      aria-pressed={isActive}
      aria-expanded={hasSubcategories ? isExpanded : undefined}
      className={clsx(
        "px-4 py-2 rounded-full flex items-center gap-2 text-sm font-medium whitespace-nowrap transition-colors",
        isActive ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
      )}
    >
      {hasSubcategories &&
        (isExpanded ? <MdExpandMore className="w-4 h-4" /> : <MdChevronRight className="w-4 h-4" />)}
      <span>{category.name}</span>
      <span
        className={clsx(
          "text-xs px-2 py-0.5 rounded-full",
          isActive ? "bg-orange-600 text-white" : "bg-gray-200 text-gray-600"
        )}
      >
        {category.count}
      </span>
    </button>
  );
});

MainCategoryButton.displayName = 'MainCategoryButton';

// ✅ 1. Extracted SubcategoryButton Component
interface SubcategoryButtonProps {
  subcategory: Subcategory;
  parentCategory: string;
  isActive: boolean;
  onClick: () => void;
}

const SubcategoryButton = React.memo<SubcategoryButtonProps>(({ 
  subcategory, 
  parentCategory, 
  isActive, 
  onClick 
}) => (
  <button
    onClick={onClick}
    aria-pressed={isActive}
    className={clsx(
      "px-3 py-1.5 rounded-full flex items-center gap-1.5 text-xs font-medium border whitespace-nowrap transition-all",
      isActive
        ? "bg-orange-400 text-white border-orange-500"
        : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"
    )}
  >
    <span>↳ {subcategory.name}</span>
    <span
      className={clsx(
        "text-xs px-1.5 py-0.5 rounded-full",
        isActive
          ? "bg-orange-500 text-white"
          : "bg-gray-200 text-gray-500"
      )}
    >
      {subcategory.count}
    </span>
  </button>
));

SubcategoryButton.displayName = 'SubcategoryButton';

// ✅ 6. Debounce utility function
function debounce(fn: () => void, delay: number) {
  let timer: ReturnType<typeof setTimeout>;
  return () => {
    clearTimeout(timer);
    timer = setTimeout(fn, delay);
  };
}

const CategoryFilter: React.FC<CategoryFilterProps> = ({
  menuItems,
  selectedCategory,
  onCategoryChange,
}) => {
  // ✅ 2. Type-safe state using object map instead of Set
  const [expandedCategories, setExpandedCategories] = React.useState<Record<string, boolean>>({});
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [showScrollIndicator, setShowScrollIndicator] = React.useState(false);

  /** ✅ Pre-filter valid items */
  const availableItems = React.useMemo(
    () =>
      menuItems.filter(
        (item) =>
          item.is_enabled &&
          item.inventory_status &&
          item.is_item_verified
      ),
    [menuItems]
  );

  /** ✅ Build category hierarchy */
  const categoryHierarchy = React.useMemo(() => {
    const hierarchy = new Map<string, CategoryHierarchy>();

    availableItems.forEach((item) => {
      const mainCategory = item.level1_category || item.category;
      const subCategory = item.level2_category;
      if (!mainCategory) return;

      if (!hierarchy.has(mainCategory)) {
        hierarchy.set(mainCategory, { name: mainCategory, count: 0, subcategories: [] });
      }

      const category = hierarchy.get(mainCategory)!;
      category.count += 1;

      if (subCategory) {
        const existing = category.subcategories.find((s) => s.name === subCategory);
        existing ? (existing.count += 1) : category.subcategories.push({ name: subCategory, count: 1 });
      }
    });

    return Array.from(hierarchy.values())
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((cat) => ({
        ...cat,
        subcategories: cat.subcategories.sort((a, b) => a.name.localeCompare(b.name)),
      }));
  }, [availableItems]);

  // ✅ 3. Moved to useCallback to avoid inline functions
  const handleAllItemsClick = React.useCallback(() => {
    onCategoryChange(null);
  }, [onCategoryChange]);

  // ✅ 3. Moved to useCallback to avoid inline functions
  const handleMainCategoryClick = React.useCallback((category: CategoryHierarchy) => {
    const isActive = selectedCategory === category.name;
    const hasSubcategories = category.subcategories.length > 0;

    // If this category is already selected, toggle expansion
    if (isActive && hasSubcategories) {
      setExpandedCategories(prev => ({
        ...prev,
        [category.name]: !prev[category.name],
      }));
    } else {
      // Filter by the main category
      onCategoryChange(category.name);
      
      // Auto-expand if it has subcategories and close other expanded categories
      if (hasSubcategories) {
        setExpandedCategories({ [category.name]: true }); // Only this category expanded
      } else {
        setExpandedCategories({}); // Close all expansions if no subcategories
      }
    }
  }, [selectedCategory, onCategoryChange]);

  // ✅ 3. Moved to useCallback to avoid inline functions
  const handleSubcategoryClick = React.useCallback((subcategoryName: string) => {
    onCategoryChange(subcategoryName);
  }, [onCategoryChange]);

  /** ✅ Scroll categories horizontally */
  const scrollCategories = React.useCallback(() => {
    scrollContainerRef.current?.scrollBy({ left: 200, behavior: "smooth" });
  }, []);

  /** ✅ 6. Debounced scroll indicator update */
  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const updateScrollIndicator = () => {
      setShowScrollIndicator(container.scrollWidth > container.clientWidth);
    };

    const debouncedUpdate = debounce(updateScrollIndicator, 200);
    window.addEventListener("resize", debouncedUpdate);

    updateScrollIndicator(); // Initial call

    return () => window.removeEventListener("resize", debouncedUpdate);
  }, [categoryHierarchy]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center gap-2">
        <div ref={scrollContainerRef} className="flex md:flex-wrap overflow-x-auto no-scrollbar gap-3 pb-2 flex-1">
          {/* ✅ All Items */}
          <div className="flex-shrink-0">
            <AllItemsButton
              isActive={selectedCategory === null}
              itemCount={availableItems.length}
              onClick={handleAllItemsClick}
            />
          </div>

          {/* ✅ Main + Subcategories */}
          {categoryHierarchy.map((cat) => (
            <React.Fragment key={cat.name}>
              <div className="flex-shrink-0">
                <MainCategoryButton
                  category={cat}
                  isActive={selectedCategory === cat.name}
                  isExpanded={!!expandedCategories[cat.name]}
                  onClick={() => handleMainCategoryClick(cat)}
                />
              </div>
              {cat.subcategories.length > 0 &&
                expandedCategories[cat.name] &&
                cat.subcategories.map((sub) => (
                  <div key={`${cat.name}-${sub.name}`} className="flex-shrink-0">
                    <SubcategoryButton
                      subcategory={sub}
                      parentCategory={cat.name}
                      isActive={selectedCategory === sub.name}
                      onClick={() => handleSubcategoryClick(sub.name)}
                    />
                  </div>
                ))}
            </React.Fragment>
          ))}
        </div>

        {/* ✅ Scroll Indicator */}
        {showScrollIndicator && (
          <button
            onClick={scrollCategories}
            className="md:hidden flex items-center text-orange-500"
            aria-label="Scroll categories"
          >
            <MdChevronRight className="w-5 h-5 animate-pulse" />
          </button>
        )}
      </div>
    </div>
  );
};

export default React.memo(CategoryFilter);
